import { Point } from "@nova/data-create-polygon-kml"
import { geometryParsers, parseKMLFile, parseKMZFile } from "../perimeters/files"
import { kml } from "@tmcw/togeojson"
import { opacityDecimalToHex } from "@nova/util-strings"
import {
    GeoJsonGeometryTypes,
    Position,
    Feature,
    Geometry,
    GeoJsonProperties,
    FeatureCollection,
} from "geojson"
import { get } from "nodash"

export const getFeaturesFromFile = async (file: File): Promise<ParsedKMLFeature[]> => {
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (extension === "kml") {
        const content = await parseKMLFile(file)
        return getComplexGeojsonFromKmlString(content)
    } else if (extension === "kmz") {
        const content = await parseKMZFile(file)
        return (await Promise.all(content.map((d) => getComplexGeojsonFromKmlString(d)))).flat()
    } else {
        throw "invalid file type provided to getFeaturesFromFile"
    }
}

interface ParsedKMLFeature {
    geometry: Point[]
    type?: GeoJsonGeometryTypes
    coordinate?: Position
    holes?: Point[][]
    color?: string
    fill?: string
    name?: string
}

const splitGeometryCollection = (
    content: FeatureCollection<Geometry | null, GeoJsonProperties>
) => {
    const features: Feature<Geometry | null, GeoJsonProperties>[] = []
    content.features.forEach((feature) => {
        if (feature.geometry?.type === "GeometryCollection") {
            const geometries = feature.geometry.geometries
            geometries?.forEach((geometry) => {
                if (geometry.type === "Point") return
                features.push({ ...feature, geometry })
            })
        } else {
            features.push(feature)
        }
    })
    return features
}

export const getComplexGeojsonFromKmlString = (kmlString: string) => {
    const content = kml(new DOMParser().parseFromString(kmlString, "text/xml"))

    //we want to handle collections of geometries like they're individual layers
    const splitCollections = splitGeometryCollection(content)
    const parsedFeatures: ParsedKMLFeature[] = []
    splitCollections.forEach((feature) => {
        const geometry =
            geometryParsers[feature.geometry?.type || ""]?.(feature.geometry)?.map((polygon) =>
                polygon.map((coords) => [coords[0], coords[1]] as [number, number])
            ) || []

        // Pull color, fill, name out of the properties
        const color = feature.properties?.stroke
        const fill = feature.properties?.fill
        const name = feature.properties?.name
        const fillOpacity = opacityDecimalToHex(feature.properties?.["fill-opacity"])
        const [primaryGeometry, ...holes] = geometry

        //we currently only support features with geometries (eg. we don't support Folder features right now)
        if (!feature.geometry) {
            return
        }
        parsedFeatures.push({
            geometry: primaryGeometry,
            coordinate:
                feature.geometry?.type === "Point" ? feature.geometry.coordinates : undefined,
            type: feature.geometry?.type,
            holes,
            color,
            fill: fill ? fill + fillOpacity : undefined,
            name,
        })
    })
    return parsedFeatures
}

export const validateFeatureFileUpload = async (files: File[]) => {
    if (files.length > 1) console.warn("Multiple files uploaded when there should only be one")
    const file = files[0]
    try {
        const extension = file.name.split(".").pop()?.toLowerCase()
        if (extension === "kml") {
            const content = await parseKMLFile(file)
            const features = getComplexGeojsonFromKmlString(content)
            if (!features || features.length === 0)
                throw new Error("Could not find valid polygons in kml")
            if (!features.every((feature) => feature.geometry?.length > 0 || !!feature.coordinate))
                throw new Error("KML contains invalid polygons")
        } else if (extension === "kmz") {
            const content = await parseKMZFile(file)
            const hasAnyValidKMLs = content.some((c) => {
                const features = getComplexGeojsonFromKmlString(c)
                const hasValidKMLs =
                    features?.length > 0 &&
                    features.every(
                        (feature) => feature.geometry?.length > 0 || !!feature.coordinate
                    )
                return hasValidKMLs
            })
            if (!hasAnyValidKMLs) throw new Error("KMZ contains no valid KMLs")
        } else {
            throw "invalid file type provided to validateFeatureFileUpload"
        }
        return [true, ""]
    } catch (err) {
        console.error(err)
        return [false, get(err, "message") || ""]
    }
}
